import { sendGAEvent } from '../utils';

enum SearchGamesEventsEnum {
  SEARCH_OVERALL_APPEARANCE = 'search_overall_appearance',
  SEARCH_RESULT_APPEARANCE = 'search_result_appearance',
  SEARCH_RESULT_GAME_OPEN = 'search_result_game_open',
}

type TSearchOverallAppearance =
  | 'search'
  | 'filter'
  | 'tabbar'
  | 'menu'
  | 'game_page';

type TSearchResultAppearanceStatus = 'success' | 'empty' | 'error';

interface ISearchResultAppearanceEventData {
  search_text: string;
  result?: TSearchResultAppearanceStatus;
  error_text?: string;
}

interface ISearchResultGameOpenEventData {
  provider: string;
  game_name: string;
}

/**
 *
 * @param { TSearchOverallAppearanceType } type  - The type of search form appearance.
 * @description This function sends a Google Analytics event when the search form appears.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `search_overall_appearance` and the category is `search`.
 */
export const sendSearchOverallAppearanceEvent = (
  type: TSearchOverallAppearance
) => {
  sendGAEvent(SearchGamesEventsEnum.SEARCH_OVERALL_APPEARANCE, {
    event_category: 'search',
    type: type,
  });
};

/**
 *
 * @param {ISearchResultAppearanceEventData} params - Event payload.
 * @param {string} params.search_text - Search text that user entered in search input.
 * @param {TSearchResultAppearanceStatus} [params.result='success'] -
 * success - more than 0 results returned
 * empty_results - no errors, but 0 results returned
 * error - no results returned due to an error (ex, Unprocessable Entity, Too Many Requests)
 * @param {string} [params.error_text] - Optional error description when the search fails.
 * @description This function sends a Google Analytics event when the search form is submitted.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `search_result_appearance` and the category is `search`.
 */

export const sendSearchResultAppearanceEvent = ({
  search_text,
  result,
  error_text,
}: ISearchResultAppearanceEventData) => {
  sendGAEvent(SearchGamesEventsEnum.SEARCH_RESULT_APPEARANCE, {
    event_category: 'search',
    search_text,
    status: result || 'success',
    ...(result === 'error' && error_text ? { error_text } : {}),
  });
};

/**
 *
 * @param {ISearchResultGameOpenEventData} params - Event payload.
 * @param {string} params.provider - The game provider name.
 * @param {string} params.game_name - The name of the game that was opened.
 * @description This function sends a Google Analytics event when user opens the game from the search results.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `search_result_game_open` and the category is `search`.
 */
export const sendSearchResultGameOpenEvent = ({
  provider,
  game_name,
}: ISearchResultGameOpenEventData) => {
  sendGAEvent(SearchGamesEventsEnum.SEARCH_RESULT_GAME_OPEN, {
    event_category: 'search',
    provider,
    game_name,
  });
};
